import {useCallback, useMemo} from "react";
import {UseSearchResult} from "../use-search";


interface UseInfiniteScrollSearchUtilsArgs {
    searchData: UseSearchResult;
    dataLength: number
}

interface UseInfiniteScrollSearchUtilsResult {
    loadMoreData: VoidCallback;
    loadPageData: (pageNumber: number) => void;
    hasMoreToLoad: boolean;
}

/**
 * Provides the common functionality for the infinite-scroller logic of the application
 * @param dataLength
 * @param searchData
 */
const useInfiniteScrollSearchUtils = ({
                                          dataLength,
                                          searchData
                                      }: UseInfiniteScrollSearchUtilsArgs): UseInfiniteScrollSearchUtilsResult => {
    const {setLoading, setPaginationInfo} = searchData;

    /**
     * Handles loading more data based on the infinite scroll by increasing the [paginationInfo]'s [currentPage] and setting the [loading] state.
     */
    const loadMoreData = useCallback<UseInfiniteScrollSearchUtilsResult['loadMoreData']>(() => {
        if (!dataLength || searchData.loading)
            return;
        setLoading?.(true);
        setPaginationInfo?.(prevState => ({
            ...prevState!,
            currentPage: (prevState?.currentPage ?? 0) + 1,
        }))
    }, [dataLength, searchData.loading, setLoading, setPaginationInfo])

    const loadPageData = useCallback((pageNumber: number) => {
        if (!dataLength || searchData.loading)
            return;
        setLoading?.(true);
        setPaginationInfo?.(prevState => ({
            ...prevState!,
            currentPage: pageNumber,
        }))
    }, [dataLength, searchData.loading, setLoading, setPaginationInfo])

    /**
     * Determines whether we can load more data.
     */
    const hasMoreToLoad = useMemo<UseInfiniteScrollSearchUtilsResult['hasMoreToLoad']>(() => {
        return !searchData.loading
            && !!searchData.paginationInfo
            && searchData.paginationInfo?.length > searchData.paginationInfo?.pageSize * searchData.paginationInfo?.currentPage
    }, [searchData.paginationInfo, searchData.loading])


    return useMemo(() => ({
        loadMoreData,
        loadPageData,
        hasMoreToLoad,
    }), [
        loadMoreData,
        hasMoreToLoad,
        loadPageData,
    ])
}

export default useInfiniteScrollSearchUtils;
